// src/components/ListingRow.js
import React, { useMemo, useEffect, useState } from 'react';
import { differenceInDays, parseISO, format, isWeekend } from 'date-fns';
import ReservationBlock from './CalendarReservationBlock';
// Remove the import since we no longer need to fetch cleaners individually
// import { getCleanerForReservation } from '../supabase';
import './CalendarListingRow.css';

// Memoize the entire component to prevent unnecessary re-renders
const ListingRow = React.memo(({ listing, dates, visibleDateRange, onReservationClick, cleanerAssignments, pricingData }) => {
    // We don't need this state anymore as we get assignments from props
    
    // Create a copy reference for the cleanerAssignments to help with memoization
    const currentCleanerAssignments = useMemo(() => {
        return { ...cleanerAssignments };
    }, [cleanerAssignments]);

    // Determine listing status (example logic - adjust based on your data)
    const listingStatus = useMemo(() => {
        if (!listing) return 'inactive';
        
        // This is just example logic - replace with your actual status determination
        if (listing.status === 'maintenance') return 'maintenance';
        if (listing.status === 'inactive') return 'inactive';
        return 'active';
    }, [listing]);

    // Only render reservations that are visible or close to visible (with buffer)
    const visibleReservations = useMemo(() => {
        if (!listing.reservations || listing.reservations.length === 0) return [];
        if (!visibleDateRange || !visibleDateRange.start || !visibleDateRange.end) return [];
        
        // With larger date ranges, we need to be more efficient
        const visibleStart = visibleDateRange.start;
        const visibleEnd = visibleDateRange.end;
        
        // Filter reservations that are within the visible date range (with buffer)
        return listing.reservations.filter(reservation => {
            const arrival = parseISO(reservation.arrival_date);
            const departure = parseISO(reservation.departure_date);
            
            // Fast overlap check (more efficient than checking each date)
            return arrival <= visibleEnd && departure >= visibleStart;
        });
    }, [listing.reservations, visibleDateRange]);

    // Generate date cells for pricing display
    const dateCells = useMemo(() => {
        if (!dates || dates.length === 0) return [];

        // More detailed logging
        const priceEntryCount = pricingData ? Object.keys(pricingData).length : 0;
        console.log(`[ListingRow ${listing.listingId}] Rendering with ${priceEntryCount} price entries`);
        
        // Log some sample pricing data for debugging
        if (priceEntryCount > 0) {
            const priceSamples = Object.entries(pricingData).slice(0, 3);
            console.log('Sample pricing data:', 
                priceSamples.map(([date, info]) => `${date}: $${info.price}`).join(', '));
        }
        
        return dates.map((date, index) => {
            // Format the date consistently as YYYY-MM-DD
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            
            const isWeekendDay = isWeekend(date);
            const priceInfo = pricingData && pricingData[dateStr];
            
            // For debugging, log a few cells that should have pricing
            if (index % 50 === 0 && priceInfo) {
                console.log(`[Date cell ${dateStr}] has price: $${priceInfo.price}`);
            } else if (index % 50 === 0 && !priceInfo) {
                console.log(`[Date cell ${dateStr}] has NO price data`);
            }
            
            const isBlocked = priceInfo && priceInfo.status === 'blocked';
            const formattedDate = format(date, 'MMM d, yyyy');
            
            return (
                <div 
                    key={dateStr} 
                    className={`calendar-date-cell ${isWeekendDay ? 'weekend' : ''} ${isBlocked ? 'blocked' : ''}`}
                    title={isBlocked ? `${formattedDate} - Blocked` : (priceInfo?.price ? `${formattedDate} - $${priceInfo.price}` : formattedDate)}
                    data-price={priceInfo?.price || 'none'}
                    data-date={dateStr}
                >
                    {priceInfo && (
                        <div className="calendar-date-price">
                            {priceInfo.price ? `$${priceInfo.price}` : ''}
                        </div>
                    )}
                </div>
            );
        });
    }, [dates, pricingData, listing.listingId]);

    // Function to calculate cleaner avatar position - memoize to avoid recalculation
    const getCleanerAvatarPositions = useMemo(() => {
        const positions = {};
        
        if (!visibleReservations || visibleReservations.length === 0) return positions;
        
        visibleReservations.forEach(reservation => {
            // Parse the dates and normalize to start of day to avoid time-of-day issues
            const arrival = parseISO(reservation.arrival_date);
            const departure = parseISO(reservation.departure_date);
            
            // Fast position calculation - based on date string comparison instead of finding in array
            const arrivalStr = format(arrival, 'yyyy-MM-dd');
            let daysFromStart = 0;
            
            // Use binary search instead of linear search for better performance
            let low = 0;
            let high = dates.length - 1;
            
            while (low <= high) {
                const mid = Math.floor((low + high) / 2);
                const dateStr = format(dates[mid], 'yyyy-MM-dd');
                
                if (dateStr === arrivalStr) {
                    daysFromStart = mid;
                    break;
                } else if (dateStr < arrivalStr) {
                    low = mid + 1;
                } else {
                    high = mid - 1;
                }
            }
            
            // Duration is calculated as difference for width
            const reservationDuration = differenceInDays(departure, arrival);
            
            // Calculate position based on reservation end
            const left = (daysFromStart * 60) + 30 + ((reservationDuration * 60) - 40) + 15;
            
            positions[reservation.id] = {
                left: `${left}px`
            };
        });
        
        return positions;
    }, [visibleReservations, dates]);

    // Reduce DOM nodes by removing unnecessary divs and elements
    return (
        <div className="listing-row">
            <div className="listing-name">
                <div className={`listing-status ${listingStatus}`}></div>
                <div className="listing-title">{listing.listingName}</div>
            </div>
            <div className="listing-calendar">
                {/* Render date cells with pricing */}
                <div className="calendar-date-cells">
                    {dateCells}
                </div>
                
                {/* Render only visible reservations */}
                {visibleReservations && visibleReservations.length > 0 && visibleReservations.map((reservation) => (
                    <React.Fragment key={reservation.id}>
                        <ReservationBlock
                            reservation={reservation}
                            dates={dates}
                            status={reservation.status || 'confirmed'}
                            onClick={() => onReservationClick && onReservationClick(reservation)}
                            displayCleanerIndicator={false} // Don't show cleaner in reservation block
                            cleanerAssignment={currentCleanerAssignments[reservation.id]} // Pass the appropriate cleaner
                        />
                        
                        {/* Render the cleaner avatar conditionally */}
                        {currentCleanerAssignments && currentCleanerAssignments[reservation.id] && (
                            <div 
                                className="calendar-cleaner-avatar"
                                style={{
                                    ...getCleanerAvatarPositions[reservation.id],
                                    backgroundColor: currentCleanerAssignments[reservation.id].color && 
                                        (currentCleanerAssignments[reservation.id].color.startsWith('#') 
                                        ? currentCleanerAssignments[reservation.id].color 
                                        : `#${currentCleanerAssignments[reservation.id].color}`)
                                }}
                                title={`Assigned to ${currentCleanerAssignments[reservation.id].first_name} ${currentCleanerAssignments[reservation.id].last_name}`}
                                onClick={() => onReservationClick && onReservationClick(reservation)}
                            >
                                {currentCleanerAssignments[reservation.id].initials}
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
});

export default ListingRow;
